import React from 'react';
import { Shield, Cookie, Eye, Globe, Database, Lock } from 'lucide-react';

import { Banner } from './Banner';

export const PrivacyPolicy = () => {
  return (
    <div className="page-content">
      {/* Shared Banner + Pill (reduced) with top spacing */}
      <Banner pillText="Privacy Policy" reduced={true} withTopSpace={true} />
      {/* Hero Section */}
      <section className="pad-120 bg-gradient-to-br from-black to-gray-900">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <Shield style={{ color: 'var(--brand-primary)' }} size={64} className="mx-auto mb-6" />
            <h1 className="heading-1 mb-6">
              Privacy Policy
            </h1>
            <p className="body-large mb-8">
              How we collect, use, and protect your personal information when you visit our website.
            </p>
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <p className="body-medium text-gray-300">
                <strong>Effective Date:</strong> January 1, 2024 | <strong>Last Updated:</strong> January 1, 2024
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pad-120">
        <div className="container max-w-4xl mx-auto">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Our Commitment to Your Privacy
            </h2>
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
              <p className="body-medium text-gray-300 mb-4">
                At <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy ("we," "our," or "us"), we are committed to protecting 
                your privacy and ensuring the security of your personal information. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you visit 
                our website or use our services.
              </p>
              <p className="body-medium text-gray-300">
                <strong>Note:</strong> This Privacy Policy covers our website and online interactions. 
                For information about how we handle your protected health information as a healthcare provider, 
                please see our <a href="/hipaa-notice" className="text-lime-400 hover:underline">HIPAA Notice of Privacy Practices</a>.
              </p>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Information We Collect
            </h2>
            
            <div className="space-y-8">
              <div className="info-type bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-start mb-4">
                  <Eye style={{ color: 'var(--brand-primary)' }} size={32} className="mr-4 mt-1" />
                  <div>
                    <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Information You Provide</h3>
                    <p className="body-medium text-gray-300 mb-4">
                      We collect information you voluntarily provide when you:
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Contact us through our website forms</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Request appointments or consultations</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Subscribe to our newsletter or blog updates</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Communicate with us via email or phone</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="info-type bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-start mb-4">
                  <Globe style={{ color: 'var(--brand-primary)' }} size={32} className="mr-4 mt-1" />
                  <div>
                    <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Automatically Collected Information</h3>
                    <p className="body-medium text-gray-300 mb-4">
                      When you visit our website, we automatically collect:
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>IP address and location information</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Browser type and version</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Pages visited and time spent on our site</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Referring website or search terms</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Device and operating system information</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="info-type bg-gray-900 rounded-xl p-6 border border-gray-800">
                <div className="flex items-start mb-4">
                  <Cookie style={{ color: 'var(--brand-primary)' }} size={32} className="mr-4 mt-1" />
                  <div>
                    <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Cookies and Tracking Technologies</h3>
                    <p className="body-medium text-gray-300 mb-4">
                      We use cookies and similar technologies to:
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Remember your preferences and settings</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Analyze website traffic and usage patterns</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Improve our website functionality</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        <span>Provide relevant content and advertisements</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              How We Use Your Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="use-case bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Service Provision</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Respond to your inquiries and requests</li>
                  <li>• Schedule appointments and consultations</li>
                  <li>• Provide customer support</li>
                  <li>• Process payments (when applicable)</li>
                </ul>
              </div>

              <div className="use-case bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Communication</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Send appointment confirmations</li>
                  <li>• Share health and wellness information</li>
                  <li>• Notify you of service updates</li>
                  <li>• Send newsletters (with your consent)</li>
                </ul>
              </div>

              <div className="use-case bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Website Improvement</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Analyze usage patterns and trends</li>
                  <li>• Improve website functionality</li>
                  <li>• Optimize user experience</li>
                  <li>• Debug technical issues</li>
                </ul>
              </div>

              <div className="use-case bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Legal Compliance</h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Comply with legal obligations</li>
                  <li>• Respond to lawful requests</li>
                  <li>• Protect our rights and property</li>
                  <li>• Ensure user safety and security</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Information Sharing */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Information Sharing and Disclosure
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-6">
              <p className="body-medium text-gray-300 mb-4">
                <strong>We do not sell, rent, or trade your personal information to third parties.</strong> 
                We may share your information only in the following circumstances:
              </p>
            </div>

            <div className="space-y-6">
              <div className="sharing-case border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Service Providers</h3>
                <p className="body-medium text-gray-300">
                  With trusted third parties who assist us in operating our website, conducting business, 
                  or servicing you (e.g., hosting providers, email services, analytics tools).
                </p>
              </div>

              <div className="sharing-case border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Legal Requirements</h3>
                <p className="body-medium text-gray-300">
                  When required by law, court order, or government regulation, or to protect our rights, 
                  property, or safety, or that of others.
                </p>
              </div>

              <div className="sharing-case border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Business Transfers</h3>
                <p className="body-medium text-gray-300">
                  In connection with a merger, acquisition, or sale of all or a portion of our business, 
                  your information may be transferred to the new owner.
                </p>
              </div>

              <div className="sharing-case border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>With Your Consent</h3>
                <p className="body-medium text-gray-300">
                  We may share your information with your explicit consent for specific purposes 
                  not covered in this policy.
                </p>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Data Security
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="security-feature bg-gray-900 rounded-xl p-6 border border-gray-800">
                <Lock style={{ color: 'var(--brand-primary)' }} size={32} className="mb-4" />
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Encryption</h3>
                <p className="body-small text-gray-400">All data transmission is encrypted using SSL/TLS protocols.</p>
              </div>

              <div className="security-feature bg-gray-900 rounded-xl p-6 border border-gray-800">
                <Database style={{ color: 'var(--brand-primary)' }} size={32} className="mb-4" />
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Secure Storage</h3>
                <p className="body-small text-gray-400">Information is stored on secure servers with restricted access.</p>
              </div>

              <div className="security-feature bg-gray-900 rounded-xl p-6 border border-gray-800">
                <Shield style={{ color: 'var(--brand-primary)' }} size={32} className="mb-4" />
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Regular Updates</h3>
                <p className="body-small text-gray-400">We regularly update our security measures and protocols.</p>
              </div>
            </div>

            <div className="mt-8 bg-gray-900 rounded-xl p-6 border border-gray-800">
              <p className="body-medium text-gray-300">
                While we implement appropriate security measures to protect your information, 
                no method of transmission over the internet or electronic storage is 100% secure. 
                We cannot guarantee absolute security but are committed to protecting your information 
                to the best of our ability.
              </p>
            </div>
          </div>

          {/* Your Choices */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Your Choices and Rights
            </h2>
            
            <div className="space-y-6">
              <div className="choice-item bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Email Communications</h3>
                <p className="body-medium text-gray-300">
                  You can opt-out of promotional emails by clicking the "unsubscribe" link in any email 
                  or contacting us directly. Note that you may still receive transactional emails 
                  related to your appointments or services.
                </p>
              </div>

              <div className="choice-item bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Cookies</h3>
                <p className="body-medium text-gray-300">
                  Most web browsers automatically accept cookies, but you can modify your browser 
                  settings to decline cookies. Note that disabling cookies may affect website functionality.
                </p>
              </div>

              <div className="choice-item bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Access and Correction</h3>
                <p className="body-medium text-gray-300">
                  You may request access to, correction of, or deletion of your personal information 
                  by contacting us using the information provided below.
                </p>
              </div>
            </div>
          </div>

          {/* Third-Party Services */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Third-Party Services
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-4">
                Our website may contain links to third-party websites or integrate with third-party services. 
                This Privacy Policy does not apply to these external sites or services. We encourage you to 
                review the privacy policies of any third-party sites you visit.
              </p>
              <p className="body-medium text-gray-300">
                We may use third-party analytics services (such as Google Analytics) to help us understand 
                how our website is used. These services may collect and use information about your visits 
                according to their own privacy policies.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Contact Us
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <p className="body-medium text-gray-300 mb-6">
                If you have questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Email</h3>
                  <p className="text-gray-300">Info@gymratrehab.com</p>
                </div>
                <div>
                  <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Phone</h3>
                  <p className="text-gray-300">(480) 555-0123</p>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Address</h3>
                <p className="text-gray-300">
                  <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy<br />
                  Located inside <a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a><br />
                  Gilbert, AZ 85234
                </p>
              </div>
            </div>
          </div>

          {/* Changes to Policy */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Changes to This Privacy Policy
            </h2>
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <p className="body-medium text-gray-300">
                We may update this Privacy Policy from time to time to reflect changes in our practices 
                or for other operational, legal, or regulatory reasons. We will post the updated policy 
                on this page and update the "Last Updated" date. We encourage you to review this policy 
                periodically to stay informed about how we protect your information.
              </p>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};